/**
 * The two player pools the draft board searches, exposed so they can be checked before draft
 * night rather than discovered to be wrong during it.
 *
 * A manager gets read access — "is Jeremiah Smith in there, and is his school right?" is a fair
 * question and a cheap answer. Refreshing is admin-only because it replaces the pool for the
 * whole league.
 */

import { Linkify } from './RuleText.js';
import { ClubChip } from './chips.js';
import { splitName } from '../domain/keeperRow.js';
import { useEffect, useMemo, useState } from 'react';
import {
  BUNDLED_BANK, POSITIONS, STATUS_LABEL, isUnsigned, refreshFromSleeper, searchPlayers,
  positionsOf, type PlayerBank,
} from '../domain/playerBank.js';
import {
  BUNDLED_PROSPECTS, mergeProspects, parseProspects, refreshFromRivals, searchProspects,
  type ProspectBank,
} from '../domain/prospectBank.js';
import { atLeast, type LeagueStore, type Role } from '../store/types.js';
import { idToken } from '../store/session.js';

const SEASON = 2026;

export function Indexes({ role, store }: { role: Role; store: LeagueStore }) {
  const [which, setWhich] = useState<'nfl' | 'college'>('nfl');
  const [bank, setBank] = useState<PlayerBank>(BUNDLED_BANK);
  const [prospects, setProspects] = useState<ProspectBank>(BUNDLED_PROSPECTS);
  const [paste, setPaste] = useState('');
  const [merged, setMerged] = useState<string | null>(null);
  const [includeHs, setIncludeHs] = useState(true);
  const [q, setQ] = useState('');
  const [pos, setPos] = useState<string[]>([]);
  const [state, setState] = useState<'idle' | 'busy' | 'ok' | 'fail'>('idle');
  /* The college board's own refresh has its own state: two cards, two verdicts. */
  const [cstate, setCstate] = useState<'idle' | 'busy' | 'ok' | 'fail'>('idle');
  const [csay, setCsay] = useState('');
  /** Quick filter: the four positions this league actually drafts people at. */
  const [skillOnly, setSkillOnly] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const isAdmin = atLeast(role, 'admin');

  useEffect(() => {
    let live = true;
    const load = () => Promise.all([store.playerBank(SEASON), store.prospectBank(SEASON)])
      .then(([b, pb]) => { if (!live) return; if (b) setBank(b); if (pb) setProspects(pb); });
    void load();
    const off = store.subscribe(SEASON, (what) => {
      if (what === 'playerBank' || what === 'prospectBank') void load();
    });
    return () => { live = false; off(); };
  }, [store]);

  const SKILL = ['QB', 'RB', 'WR', 'TE'];
  const effectivePos = skillOnly ? (pos.length ? pos.filter((x) => SKILL.includes(x)) : SKILL) : pos;
  const nfl = useMemo(
    () => searchPlayers(bank, q, { positions: effectivePos, limit: showAll ? 5000 : 400 }),
    [bank, q, effectivePos.join(','), showAll],
  );
  const college = useMemo(
    () => searchProspects(prospects, q, { positions: effectivePos, includeHs }),
    [prospects, q, effectivePos.join(','), includeHs],
  );
  const preview = useMemo(() => parseProspects(paste), [paste]);

  const counts = useMemo(() => bank.players.reduce<Record<string, number>>(
    (m, p) => ({ ...m, [p.pos]: (m[p.pos] ?? 0) + 1 }), {},
  ), [bank]);

  async function applyPaste() {
    const { bank: next, added, updated } = mergeProspects(prospects, preview.parsed, role === 'admin' ? 'ADMIN' : 'COMMISH');
    await store.saveProspectBank(next);
    setProspects(next);
    setPaste('');
    setMerged(`${added} added, ${updated} updated`);
    setTimeout(() => setMerged(null), 4000);
  }

  /** Throw away every paste and go back to what shipped with the build. */
  async function revert() {
    await store.saveProspectBank(BUNDLED_PROSPECTS);
    setProspects(BUNDLED_PROSPECTS);
    setMerged('Reverted to the bundled board');
    setTimeout(() => setMerged(null), 4000);
  }

  /*
   * THE RIVALS300, PULLED RATHER THAN TRANSCRIBED (Tim, 8 September). The bundled 2027 board was
   * typed in by hand off three big boards, because nothing serves this as data — but the on3
   * Rivals rankings are one page of three hundred recruits, and a function can read them.
   *
   * It MERGES, like the paste box beside it: nobody is deleted, the hand-transcribed ranks stay,
   * and a name already on the board gains the Rivals fields rather than being replaced. So this
   * is safe to press twice, and safe to press after a paste.
   */
  async function refreshCollege() {
    setCstate('busy');
    setCsay('');
    try {
      const rows = await refreshFromRivals(idToken);
      const { bank: next, added, updated } = mergeProspects(prospects, rows, 'RIVALS');
      await store.saveProspectBank(next);
      setProspects(next);
      setCstate('ok');
      setCsay(`${rows.length} read · ${added} added, ${updated} updated`);
    } catch (e) {
      setCstate('fail');
      setCsay(e instanceof Error ? e.message : 'Could not read the Rivals board');
    }
  }

  async function refresh() {
    setState('busy');
    try {
      const fresh = await refreshFromSleeper();
      await store.savePlayerBank(fresh);
      setBank(fresh);
      setState('ok');
    } catch {
      setState('fail');
    }
  }

  const POS_CHOICES = which === 'nfl' ? POSITIONS : ['QB', 'RB', 'WR', 'TE'];
  const readOn = (iso: string) => new Date(`${iso.slice(0, 10)}T12:00:00Z`)
    .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  /** What the status column says: a sentence, never a pill zoo (Pass 16). */
  const statusSays = (p: (typeof nfl)[number]): string => [
    p.status && p.status !== 'ACT' ? (STATUS_LABEL[p.status] ?? p.status) : '',
    !p.team && p.last ? `ex-${p.last}` : '',
  ].filter(Boolean).join(' · ');
  const firstSkip = preview.skipped[0];

  return (
    <div className="app admin pindex">
      <Linkify>
        <div className="pooltools">
          <span className="seg" role="tablist" aria-label="Which pool">
            <button type="button" role="tab" aria-selected={which === 'nfl'} className={which === 'nfl' ? 'on' : ''}
              onClick={() => setWhich('nfl')}>NFL</button>
            <button type="button" role="tab" aria-selected={which === 'college'} className={which === 'college' ? 'on' : ''}
              onClick={() => setWhich('college')}>College</button>
          </span>
          <input className="poolsearch" type="search" aria-label="Search the pool"
            placeholder={which === 'nfl' ? 'Name, initials or team' : 'Name or school'}
            value={q} onChange={(e) => setQ(e.target.value)} />
          <span className="poolpos">
            {POS_CHOICES.map((p) => (
              <button key={p} type="button" className={`kindchip ${pos.includes(p) ? 'on' : ''}`}
                onClick={() => setPos((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]))}>
                {p}
              </button>
            ))}
          </span>
          <label className="poolcheck">
            <input type="checkbox" checked={skillOnly} onChange={(e) => setSkillOnly(e.target.checked)} />
            fantasy positions only
          </label>
          {(pos.length > 0 || q) && (
            <button type="button" className="linkbtn poolclear" onClick={() => { setPos([]); setQ(''); }}>clear</button>
          )}
        </div>

        {which === 'nfl' ? (
          <>
            {/*
              THE REFRESH IS ABOVE THE LIST NOW (Tim, 8 September: "i lost my NFL player index
              refresh. I need that back"). It was never removed — it sat under fourteen hundred
              rows, at the bottom of a page whose first screen is a search field and four hundred
              players, and a control you have to scroll a thousand rows to reach is lost whether
              or not it is rendered. The list is the reference; this is the thing you came to
              press.
            */}
            {isAdmin && (
              <section className="card refreshcard">
                <div className="refreshrow">
                  <div>
                    <h3>Refresh from Sleeper</h3>
                    <p className={`fact ${state === 'fail' ? 'warnline' : ''}`}>
                      {state === 'fail' ? 'Refresh failed · still on the bundled snapshot'
                        : state === 'ok' ? 'Updated · live rosters, players who moved are current'
                          : 'Pulls live rosters when players move. Bye weeks come from the schedule either way.'}
                    </p>
                  </div>
                  <span className="pill amber">Admin · replaces the pool</span>
                  <button type="button" className="btn" disabled={state === 'busy'} onClick={() => void refresh()}>
                    {state === 'busy' ? 'Refreshing…' : 'Refresh'}
                  </button>
                </div>
              </section>
            )}
            <section className="card">
              <div className="cardhead poolhead">
                <h3>{bank.players.length.toLocaleString()} players</h3>
                <span className="pooltally">{Object.entries(counts).sort().map(([k, v]) => `${k} ${v}`).join(' · ')}</span>
                <span className="poolsrc">{/^\w+/.exec(bank.source.replace(/^bundled from\s+/i, ''))?.[0] ?? bank.source} · {readOn(bank.asOf)}</span>
              </div>
              <ol className="poolrows">
                {nfl.map((p) => {
                  /* A defence has no given name: "49ers DEF" is the surname whole. */
                  const { surname, given } = p.pos === 'DEF' ? { surname: p.name.replace(/\s+DEF$/i, ''), given: 'DEF' } : splitName(p.name);
                  const said = statusSays(p);
                  return (
                    <li key={`${p.name}|${p.pos}`} className={isUnsigned(p) ? 'dim' : ''}>
                      <span className="poolname"><b>{surname.toUpperCase()}</b> <span className="given">{given}</span></span>
                      <span className="poolchips">
                        {positionsOf(p).map((x) => <span key={x} className={`poschip pos-${x}`}>{x}</span>)}
                        <ClubChip team={p.team ?? p.last ?? null} className={p.team ? '' : 'ex'} />
                      </span>
                      <span className="poolmeta">
                        <span className="poolbye">{p.bye ? `bye ${p.bye}` : '—'}</span>
                        {said && <span className={`poolstatus ${isUnsigned(p) ? '' : 'warn'}`}>{said}</span>}
                      </span>
                    </li>
                  );
                })}
              </ol>
              <div className="poolfoot">
                {!showAll && nfl.length === 400
                  ? <>First 400 of {bank.players.length.toLocaleString()} · <button type="button" className="linkbtn" onClick={() => setShowAll(true)}>Show all</button></>
                  : showAll
                    ? <>All {nfl.length.toLocaleString()} · <button type="button" className="linkbtn" onClick={() => setShowAll(false)}>Back to the first 400</button></>
                    : <>{nfl.length.toLocaleString()} shown</>}
              </div>
            </section>

          </>
        ) : (
          <>
            {/*
              AND ONE FOR THE COLLEGE BOARD (Tim, 8 September: "Add one for college/highschool
              players including populating from the Rivals300"). Above its list for the same
              reason, and worded so the difference from the NFL card is on the card: that one
              REPLACES the pool, this one merges into the board and deletes nobody.
            */}
            {isAdmin && (
              <section className="card refreshcard">
                <div className="refreshrow">
                  <div>
                    <h3>Refresh from Rivals</h3>
                    <p className={`fact ${cstate === 'fail' ? 'warnline' : ''}`}>
                      {csay || 'Reads the Rivals300 for the 2027 class. QB, RB, WR and TE, '
                        + 'with the athletes filed as receivers.'}
                    </p>
                  </div>
                  {/*
                    GREY, NOT AMBER. The Sleeper card's amber says the thing it warns about:
                    pressing it REPLACES the pool. This one merges and deletes nobody, so an
                    identical warning beside an identical button would teach a man to read
                    neither.
                  */}
                  <span className="pill grey">Admin · merges, deletes nobody</span>
                  <button type="button" className="btn" disabled={cstate === 'busy'}
                    onClick={() => void refreshCollege()}>
                    {cstate === 'busy' ? 'Reading…' : 'Refresh'}
                  </button>
                </div>
              </section>
            )}
            <section className="card">
              <div className="cardhead poolhead">
                <h3>{prospects.prospects.length} prospects · 2027 class</h3>
                <label className="poolcheck">
                  <input type="checkbox" checked={includeHs} onChange={(e) => setIncludeHs(e.target.checked)} />
                  include high school
                </label>
                <span className="poolsrc">last read {readOn(prospects.readOn)}</span>
              </div>
              <ol className="poolrows college">
                {college.map((p) => {
                  const { surname, given } = splitName(p.n);
                  return (
                    <li key={`${p.hs ? 'hs' : ''}${p.n}`}>
                      {/* Devy leads; the draft board is the fallback, and a dash is the truth. */}
                      <span className="poolrank">{p.hs ? <><span className="hs">HS</span> #{p.hsRank}</>
                        : (p.devy ?? p.overall) ? `#${p.devy ?? p.overall}` : '—'}</span>
                      <span className="poolname"><b>{surname.toUpperCase()}</b> <span className="given">{given}</span></span>
                      <span className="poolchips">
                        <span className={`poschip pos-${p.p}`}>{p.p}</span>
                        {(p.hsSchool ?? p.s) && <span className="schoolchip">{p.hs && p.hsSchool ? p.hsSchool : p.s}</span>}
                        {/* And where devy leads, the draft board's own number moves to the side. */}
                        <span className="poolcode">{p.devy && p.overall ? `bd${p.overall}`
                          : p.posRank ? `${p.p}${p.posRank}` : p.consensus ? `c${p.consensus}` : ''}</span>
                      </span>
                      <span className="poolmark">
                        {[p.hs ? 'HS' : '', p.addedBy ? `added by ${p.addedBy}` : ''].filter(Boolean).join(' · ')}
                      </span>
                    </li>
                  );
                })}
              </ol>
              <p className="cardfoot fact">A starting bank, not a closed list — the auction accepts any typed name.</p>
            </section>

            {isAdmin && (
              <section className="card pastecard">
                <div className="cardhead">
                  <h3>Update the board</h3>
                  <span className="pill amber">Admin · replaces the board for everyone</span>
                </div>
                <div className="pastebody">
                  <div className="pastewrap">
                    <textarea
                      className="paste"
                      rows={6}
                      aria-label="Paste a board"
                      value={paste}
                      onChange={(e) => setPaste(e.target.value)}
                    />
                    {!paste && (
                      <div className="pasteempty" aria-hidden="true">
                        <span>Paste rankings — one player a line</span>
                        <code>1|Name|POS|School · commas and dashes also read</code>
                      </div>
                    )}
                  </div>
                  <div className="pasteacts">
                    <span className="pastecount">
                      {merged ?? (paste.trim() ? (
                        <>
                          <b>{preview.parsed.length} read</b> · {preview.skipped.length} skipped
                          {preview.parsed.length === 0 && preview.skipped.length > 0 && (
                            <span className="pastebad"> no line had a name and a position — nothing to merge</span>
                          )}
                          {preview.parsed.length > 0 && firstSkip && (
                            <span className="pastebad"> line {lineOf(paste, firstSkip.line)} — {firstSkip.why}</span>
                          )}
                        </>
                      ) : '')}
                    </span>
                    <span className="spacer" />
                    {prospects.sources.some((x) => x.startsWith('Pasted')) && (
                      <button type="button" className="linkbtn danger" onClick={() => void revert()}>
                        Revert to the bundled board
                      </button>
                    )}
                    <button type="button" className="btn primary" disabled={preview.parsed.length === 0}
                      onClick={() => void applyPaste()}>
                      Merge {preview.parsed.length}
                    </button>
                  </div>
                  <p className="fact">A merge deletes nobody. After: “12 added, 3 updated.”</p>
                </div>
              </section>
            )}
          </>
        )}
      </Linkify>
    </div>
  );
}

/** Which line of the paste a skipped row came from, 1-based, for the sentence under the box. */
function lineOf(text: string, line: string): number {
  const i = text.split(/\r?\n/).findIndex((l) => l.trim() === line);
  return i < 0 ? 0 : i + 1;
}
